import { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import type {
  Section,
  QIT,
  QuestionnaireSchema,
} from 'src/components/WSCertificationSummary/types/QuestionnaireSchemaTypes';
import type { Certification } from 'src/types/Certification';
import QuestionnaireGroup from './QuestionnaireGroup';
import ConditionalGlow from './ConditionalGlow';

interface QuestionnaireSectionProps {
  section: Section;
}

const QuestionnaireSection: FC<QuestionnaireSectionProps> = ({ section }) => {
  const { certificationId } = useParams<{ certificationId: string }>();
  const certId = certificationId ? Number(certificationId) : NaN;

  const [headerHeight, setHeaderHeight] = useState(48);
  const headerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setHeaderHeight(node.getBoundingClientRect().height);
    }
  }, []);

  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  const configId = useMemo(() => {
    const cert = certifications.find((c) => c.id === certId);
    return cert?.certification_config ?? 0;
  }, [certifications, certId]);

  const schema = useSelector(
    (state: RootState) => state.newRenterInterface.schemas[configId]
  ) as QuestionnaireSchema | undefined;

  const visibility = useSelector(
    (state: RootState) =>
      state.newRenterInterface.questionnaireVisibility[certId]
  );

  const visibleGroupIds = useMemo(
    () => new Set(visibility?.visibleGroupIds ?? []),
    [visibility?.visibleGroupIds]
  );

  const filteredQitIds = useMemo(
    () => new Set(visibility?.filteredQitIds ?? []),
    [visibility?.filteredQitIds]
  );

  const newlyRevealed = useMemo(
    () => new Set(visibility?.newlyRevealed ?? []),
    [visibility?.newlyRevealed]
  );

  const revealOrigin = useMemo(
    () =>
      new Map(
        Object.entries(visibility?.revealOrigin ?? {}).map(([k, v]) => [
          Number(k),
          v,
        ])
      ),
    [visibility?.revealOrigin]
  );

  const memberTypeId = visibility?.memberTypeId ?? null;
  const toolbarOffset = useSelector(
    (state: RootState) => state.newRenterUI.toolbarOffset
  );

  const sectionGroups = useMemo(() => {
    if (!schema) {
      return [];
    }
    const groupIdsInSection = new Set<number>();
    const minOrderByGroup = new Map<number, number>();
    for (const qit of schema.qits) {
      if (qit.section_id === section.id) {
        if (
          qit.member_types.length === 0 ||
          memberTypeId === null ||
          qit.member_types.includes(memberTypeId)
        ) {
          groupIdsInSection.add(qit.question_group_id);
          const prev = minOrderByGroup.get(qit.question_group_id);
          if (prev === undefined || qit.order < prev) {
            minOrderByGroup.set(qit.question_group_id, qit.order);
          }
        }
      }
    }
    return schema.question_groups
      .filter(
        (g) =>
          groupIdsInSection.has(g.id) &&
          (g.householdmember_type.length === 0 ||
            memberTypeId === null ||
            g.householdmember_type.includes(memberTypeId))
      )
      .sort(
        (a, b) =>
          (minOrderByGroup.get(a.id) ?? 0) - (minOrderByGroup.get(b.id) ?? 0)
      );
  }, [schema, section.id, memberTypeId]);

  const qitsByGroup = useMemo(() => {
    if (!schema) {
      return new Map<number, QIT[]>();
    }
    const result = new Map<number, QIT[]>();
    for (const qit of schema.qits) {
      if (qit.section_id !== section.id) {
        continue;
      }
      if (
        qit.member_types.length > 0 &&
        memberTypeId !== null &&
        !qit.member_types.includes(memberTypeId)
      ) {
        continue;
      }
      if (!result.has(qit.question_group_id)) {
        result.set(qit.question_group_id, []);
      }
      result.get(qit.question_group_id)!.push(qit);
    }
    for (const arr of result.values()) {
      arr.sort((a, b) => a.order - b.order);
    }
    return result;
  }, [schema, section.id, memberTypeId]);

  const visibleGroups = useMemo(
    () => sectionGroups.filter((g) => visibleGroupIds.has(g.id)),
    [sectionGroups, visibleGroupIds]
  );

  const hasVisibleContent = useMemo(() => {
    for (const group of visibleGroups) {
      const groupQits = qitsByGroup.get(group.id) ?? [];
      if (groupQits.some((qit) => filteredQitIds.has(qit.id))) {
        return true;
      }
    }
    return false;
  }, [visibleGroups, qitsByGroup, filteredQitIds]);

  if (!hasVisibleContent) {
    return null;
  }

  return (
    <Box sx={{ mb: 3, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box
        ref={headerRef}
        sx={{
          position: 'sticky',
          top: toolbarOffset,
          zIndex: 3,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          borderRadius: '4px 4px 0 0',
          transition: 'top 0.25s ease-in-out',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {section.name}
        </Typography>
      </Box>

      <Box sx={{ py: 1 }}>
        {visibleGroups.map((group) => {
          const groupQits = qitsByGroup.get(group.id) ?? [];
          const isGroupRevealed = newlyRevealed.has(-group.id);
          const groupOrigin = revealOrigin.get(group.id) ?? null;

          return (
            <ConditionalGlow
              key={group.id}
              isNewlyRevealed={isGroupRevealed}
              origin={groupOrigin}
            >
              <QuestionnaireGroup
                group={group}
                qits={groupQits}
                sectionHeaderHeight={headerHeight}
              />
            </ConditionalGlow>
          );
        })}
      </Box>
    </Box>
  );
};

export default QuestionnaireSection;
